import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';

import { Button } from '@autop/ui/src/button';

import { useTRPC } from '@/shared/api';
import {
  PROVIDER_LABELS,
  SOCIAL_PROVIDERS,
  type SocialProvider,
} from '@/shared/lib/social-handoff';
import { startSocialLogin } from '@/shared/lib/start-social-login';

/** 연결을 마치고 돌아올 곳. 나갈 때 쪽지에 실린다. */
const RETURN_TO = '/my/account';

/**
 * 라우트 파라미터 ↔ 서버 enum.
 *
 * `toUpperCase()` 로 만들지 않는다 — 표로 두면 제공사가 늘 때 고칠 곳이 한눈에 보이고,
 * 서버가 다른 표기를 쓰기 시작해도 여기만 고치면 된다 (`callback-status.ts` 와 같은 판단).
 */
const SERVER_PROVIDER: Record<SocialProvider, 'KAKAO' | 'NAVER'> = {
  kakao: 'KAKAO',
  naver: 'NAVER',
};

/**
 * 연결된 로그인 수단 — 소셜 계정을 붙이고 뗀다.
 *
 * **연결은 우리가 직접 하지 않는다.** 제공자에게 보냈다가 돌아오는 흐름을 한 번 타야
 * 하고(그래야 그 계정이 정말 본인 것임을 제공자가 보증한다), 돌아온 뒤의 확인 화면은
 * 이미 있다(`SocialLinkConfirm`). 여기서는 그 흐름의 입구만 연다.
 *
 * **해제는 마지막 수단인지 서버가 판단한다.** 화면이 "하나 남았으니 막자"고 세면
 * 비밀번호 보유 여부를 모르기 때문에 틀린다 — 소셜 하나뿐이어도 비밀번호가 있으면
 * 떼도 된다. 서버는 그 둘을 한 트랜잭션 안에서 함께 본다.
 */
export function LinkedAccounts(): ReactElement {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const linked = useQuery(trpc.auth.social.linked.queryOptions());

  const unlink = useMutation(
    trpc.auth.social.unlink.mutationOptions({
      // 서버가 갱신된 목록을 돌려준다 — 다시 조회하지 않고 그것으로 갈아끼운다.
      //
      // 진행 중인 조회를 취소하지 않는다. 일반적으로는 뒤늦게 도착한 재조회가 이 값을
      // 덮을 수 있지만, **이 앱에서는 그 재조회가 일어나지 않는다** — 전역 기본값이
      // `refetchOnWindowFocus: false` · `staleTime: 60초`이고(`shared/api/query-client.ts`),
      // 첫 조회가 끝나기 전에는 「해제」 버튼이 화면에 없다(연결 여부를 알아야 그려진다).
      // 기본값이 바뀌면 여기에 cancelQueries 가 필요해진다.
      onSuccess: (remaining) =>
        queryClient.setQueryData(trpc.auth.social.linked.queryKey(), remaining),
    }),
  );

  return (
    <section aria-labelledby="account-social-heading" className="flex flex-col gap-3">
      <h2 id="account-social-heading" className="text-[1.0625rem] font-semibold tracking-[-0.03em]">
        연결된 로그인 수단
      </h2>

      {linked.isError ? (
        <p role="alert" className="text-[13px] text-destructive">
          연결된 계정을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
        </p>
      ) : (
        <ul className="rounded-lg border border-border">
          {SOCIAL_PROVIDERS.map((provider) => {
            const label = PROVIDER_LABELS[provider];
            const isLinked =
              linked.data?.some((item) => item.provider === SERVER_PROVIDER[provider]) ?? false;

            return (
              <li
                key={provider}
                aria-label={label}
                className="flex flex-wrap items-center gap-3 border-border border-b p-4 last:border-b-0"
              >
                <span className="text-[14px] font-medium">{label}</span>
                <span className="flex-1 text-[12.5px] text-muted-foreground">
                  {linked.isPending ? '확인하는 중' : isLinked ? '연결됨' : '연결 안 됨'}
                </span>
                {isLinked ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={unlink.isPending}
                    onClick={() => unlink.mutate({ provider: SERVER_PROVIDER[provider] })}
                  >
                    해제
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={linked.isPending}
                    onClick={() => startSocialLogin(provider, RETURN_TO, 'link')}
                  >
                    연결
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {unlink.error === null ? null : (
        <p role="alert" className="text-[13px] text-destructive">
          {unlink.error.message}
        </p>
      )}

      <p className="text-[12.5px] leading-[1.8] text-muted-foreground">
        연결해 두면 다음부터 그 버튼으로도 로그인할 수 있습니다. 소셜 계정은 로그인에만 쓰이므로{' '}
        <b className="font-medium text-foreground">계정 이메일은 바뀌지 않습니다</b> —
        카카오·네이버에 등록된 이메일이 달라도 괜찮습니다. 로그인 수단이 하나만 남았을 때는 해제할
        수 없습니다.
      </p>
    </section>
  );
}
